'use client';

import { FC } from 'react';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { SORT_KEY, SORT_OPTIONS_FR } from '@core/Constants/Sort';

import { getCorrectRelevanceForUk } from '@core/Utils/SearchFilters/AddDefaultSortingIfMissing';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import { DropdownOptionProps } from '@gds/Dropdown/Components/DropdownOption.types';
import { Dropdown } from '@gds/Dropdown/Dropdown';
import { Typography } from '@gds/Typography/Typography';
import { useAtom, useAtomValue } from 'jotai';

import { sortDrawerOpenAtom } from 'Atoms/App/Plp/SortDrawerOpenAtom';
import { useUpdateFilters } from 'Hooks/SearchFilters/UseUpdateFilters';

import { trackFilterEvent } from '../../Filters.tracking';

import { SortProps } from './Sort.entity';
import { SortMessages } from './Sort.messages';
import SortDrawer from './SortDrawer';

import styles from './Sort.module.css';

const Sort: FC<SortProps> = ({ experiments, filterList, locale, sortMessages }) => {
  const sortFilter = getFilterFromArray(SORT_KEY, filterList);
  const filtersList = useAtomValue(filtersListAtom);
  const [sortDrawerIsOpen] = useAtom(sortDrawerOpenAtom);
  const { FINAL_SORT_ORDER_UK, FINAL_DEFAULT_SORTING } = getCorrectRelevanceForUk(experiments);

  const { updateFilters } = useUpdateFilters();

  const sortOptions = (locale === 'fr' ? SORT_OPTIONS_FR : FINAL_SORT_ORDER_UK).map(option => ({
    label: sortMessages[option as keyof SortMessages],
    value: option,
  }));

  const handleSortChange = (option: DropdownOptionProps) => {
    trackFilterEvent(SORT_KEY, option.value as string, filtersList);
    updateFilters([{ id: SORT_KEY, value: option.value as string }]);
  };

  const selectedSortValue = sortFilter
    ? sortOptions.find(option => option.value === sortFilter?.value)
    : sortOptions.find(option => option.value === FINAL_DEFAULT_SORTING[locale]);

  return (
    <>
      <div className={styles.wrapper} data-test-id="sort-draw">
        {FINAL_DEFAULT_SORTING[locale] && (
          <Typography variant="subheading3">{sortMessages.sortBy}</Typography>
        )}
        <Dropdown
          dataTestId="sort-plp"
          options={sortOptions}
          placeholder={sortMessages.sortBy}
          value={selectedSortValue}
          uncontrolled
          onChange={option => {
            option && handleSortChange(option);
          }}
        />
      </div>
      {sortDrawerIsOpen && (
        <SortDrawer
          sortByText={sortMessages.sortBy}
          options={sortOptions}
          selectedValue={selectedSortValue!}
          selectOption={handleSortChange}
        />
      )}
    </>
  );
};

export default Sort;
